import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from "recharts";
import { Users, UserCheck, UserX, Megaphone, Repeat, Sparkles, Loader2, Upload, Download } from "lucide-react";
import { Layout } from "../components/Layout";
import { AnimatedNumber } from "../components/ScoreRing";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useLang } from "../lib/i18n";
import { NOVA_DEMO } from "../lib/demoData";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { ClientCRM } from "../components/ClientCRM";

const COLORS = ["#6366F1", "#00E5FF", "#8B5CF6", "#F59E0B"];

function computeLoyalty(rows) {
  const now = Date.now();
  const DAY = 86400000;
  let active = 0, toReact = 0, totalVisits = 0;
  const seg = { "Fidèles": 0, "Réguliers": 0, "Occasionnels": 0, "À réactiver": 0 };
  rows.forEach((r) => {
    const visits = parseInt(r.visits) || 0;
    totalVisits += visits;
    const lp = r.last_purchase ? Date.parse(r.last_purchase) : NaN;
    const days = isNaN(lp) ? 9999 : Math.floor((now - lp) / DAY);
    if (days <= 60) active += 1;
    if (days > 90) { toReact += 1; seg["À réactiver"] += 1; }
    else if (visits >= 10) seg["Fidèles"] += 1;
    else if (visits >= 4) seg["Réguliers"] += 1;
    else seg["Occasionnels"] += 1;
  });
  const avg = rows.length ? (totalVisits / rows.length) : 0;
  return {
    clients: rows.length, active, to_reactivate: toReact, campaigns: 0,
    purchase_freq: `${avg.toFixed(1).replace(".", ",")} visites`,
    segments: Object.entries(seg).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value })),
    imported: true,
  };
}

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
  return lines.slice(1).map((line) => {
    const cells = line.split(",");
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (cells[i] || "").trim(); });
    return obj;
  });
}

function Metric({ icon: Icon, value, label, color, testid }) {
  return (
    <div className="lb-card lb-card-hover p-5" data-testid={testid}>
      <div className={`flex items-center gap-2 mb-2 ${color}`}><Icon size={18} /><span className="font-display font-black text-2xl"><AnimatedNumber value={value} /></span></div>
      <p className="text-xs text-[hsl(var(--muted-foreground))]">{label}</p>
    </div>
  );
}

export default function Loyalty() {
  const { data, demo, setData } = useApp();
  const { t } = useLang();
  const l = data?.loyalty || (demo ? NOVA_DEMO.loyalty : null);
  const [campaign, setCampaign] = useState("");
  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const fileRef = useRef();
  const biz = data?.onboarding?.business_name || "mon commerce";

  const genCampaign = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.post("/ai/content", { content_type: "whatsapp", tone: "chaleureux", topic: `campagne de réactivation pour ${l?.to_reactivate || "vos"} clients inactifs avec une offre de retour`, business_name: biz, action: "generate" });
      setCampaign(res.content);
      toast.success("Campagne de réactivation générée !");
    } catch { toast.error("Erreur"); } finally { setLoading(false); }
  };

  const downloadTemplate = () => {
    const csv = "name,email,last_purchase,visits\nMarie Dupont,marie@mail.com,2026-05-20,12\nPaul Martin,paul@mail.com,2026-01-10,3\nSophie Bernard,sophie@mail.com,2025-11-02,1\n";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "modele_clients_localboost.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const onImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const rows = parseCSV(String(reader.result));
        if (!rows.length) { toast.error("Fichier vide ou invalide."); return; }
        const loyalty = computeLoyalty(rows);
        setData((d) => ({ ...(d || {}), loyalty, clients: rows }));
        setImportOpen(false);
        toast.success(`${rows.length} clients importés !`);
      } catch { toast.error("Impossible de lire le fichier CSV."); }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  return (
    <Layout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div><h1 className="font-display font-black text-2xl lg:text-3xl">{t("title_loyalty")}</h1><p className="text-sm text-[hsl(var(--muted-foreground))]">Fidélisez et réactivez vos clients.</p></div>
        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogTrigger asChild>
            <Button data-testid="loy-import-btn" variant="outline" className="rounded-full border-white/10 bg-white/5 h-10 text-xs"><Upload size={14} className="mr-1.5" /> Importer mes clients</Button>
          </DialogTrigger>
          <DialogContent className="glass border-white/10" data-testid="loy-import-dialog">
            <DialogHeader>
              <DialogTitle>Importer ma base clients</DialogTitle>
              <DialogDescription className="text-xs">Fichier CSV avec les colonnes : name, email, last_purchase (AAAA-MM-JJ), visits.</DialogDescription>
            </DialogHeader>
            <button onClick={downloadTemplate} data-testid="loy-template" className="flex items-center gap-1.5 text-xs text-neon"><Download size={13} /> Télécharger le modèle CSV</button>
            <label className="mt-3 flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 bg-white/[0.02] p-8 cursor-pointer hover:border-primary/40 transition-colors" data-testid="loy-dropzone">
              <Upload size={22} className="text-neon" />
              <span className="text-sm">Choisir un fichier CSV</span>
              <input ref={fileRef} data-testid="loy-file-input" type="file" accept=".csv,text/csv" onChange={onImport} className="hidden" />
            </label>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Les données restent privées à votre compte. Elles alimentent vos segments et campagnes de réactivation.</p>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        {l ? (
          <>
            <Metric icon={Users} value={l.clients} label="Clients" color="text-neon" testid="loy-clients" />
            <Metric icon={UserCheck} value={l.active} label="Clients actifs" color="text-emerald-400" testid="loy-active" />
            <Metric icon={UserX} value={l.to_reactivate} label="À réactiver" color="text-orange-400" testid="loy-reactivate" />
            <Metric icon={Megaphone} value={l.campaigns} label="Campagnes" color="text-primary-foreground" testid="loy-campaigns" />
            <div className="lb-card lb-card-hover p-5" data-testid="loy-freq">
              <div className="flex items-center gap-2 mb-2 text-purple-400"><Repeat size={18} /><span className="font-display font-black text-xl">{l.purchase_freq}</span></div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Fréquence d'achat</p>
            </div>
          </>
        ) : (
          [["loy-clients", Users, "Clients"], ["loy-active", UserCheck, "Clients actifs"], ["loy-reactivate", UserX, "À réactiver"], ["loy-campaigns", Megaphone, "Campagnes"], ["loy-freq", Repeat, "Fréquence d'achat"]].map(([tid, Icon, label]) => (
            <div key={tid} className="lb-card p-5" data-testid={tid}>
              <div className="flex items-center gap-2 mb-2 text-[hsl(var(--muted-foreground))]"><Icon size={18} /><span className="font-display font-black text-2xl">—</span></div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">{label}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Non renseigné</p>
            </div>
          ))
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="lb-card p-5" data-testid="loy-segments">
          <h3 className="font-display font-bold text-lg mb-3">Segments clients</h3>
          {l ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={l.segments} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                    {l.segments.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="none" />)}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 12, color: "#94A3B8" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-center text-sm text-[hsl(var(--muted-foreground))]" data-testid="loy-segments-empty">Non renseigné — importez votre base clients pour visualiser vos segments.</div>
          )}
        </div>

        <div className="lb-card p-5" data-testid="loy-campaign">
          <h3 className="font-display font-bold text-lg mb-1">Campagne de réactivation</h3>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">{l ? `${l.to_reactivate} clients dormants à reconquérir.` : "Préparez un message de réactivation prêt à envoyer."}</p>
          <Button data-testid="loy-gen-campaign" onClick={genCampaign} disabled={loading} className="rounded-full bg-gradient-to-r from-primary to-neon text-white lb-glow-btn mb-4 h-10">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <><Sparkles size={15} className="mr-1" /> Créer la campagne</>}
          </Button>
          {campaign && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-xl bg-white/[0.02] border border-white/5 p-4 text-sm whitespace-pre-wrap">{campaign}</motion.div>}
        </div>
      </div>
      <ClientCRM />
    </Layout>
  );
}
